import axios, { AxiosRequestConfig } from "axios";
import ApiResponse, { IApiResponse } from "./ApiResponse";
import { IApiRequest } from "./ApiRequest";
import APIErrorCode from "./ErrorCode.json";
import auth from "./Auth";
import * as CONSTANTS from "../utilities/Constants";
import * as ACTIONCONTANTS from "../flux/actions/ActionConstants";

export interface IApi {
  login(request: IApiRequest): Promise<IApiResponse>;
  licenseGet(request: IApiRequest): Promise<IApiResponse>;
  licensePost(request: IApiRequest): Promise<IApiResponse>;
  get(request: IApiRequest): Promise<IApiResponse>;
  post(request: IApiRequest): Promise<IApiResponse>;
  put(request: IApiRequest): Promise<IApiResponse>;
  delete(request: IApiRequest): Promise<IApiResponse>;
  deleteAll(request: IApiRequest): Promise<IApiResponse>;
  patch(request: IApiRequest): Promise<IApiResponse>;
  download(
    request: IApiRequest,
    id: string,
    contentType: string
  ): Promise<IApiResponse>;
  downloadPost(
    request: IApiRequest,
    id: string[],
    contentType: string
  ): Promise<IApiResponse>;
}

const axiosInstance = axios.create({
  headers: {
    "Access-Control-Allow-Origin": "*",
  },
});

// let isRefreshToken = false;

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Prevent infinite loops
    if (
      error.response.status === 401 &&
      originalRequest.url.includes("/Refresh")
    ) {
      auth.removeTokens();
      window.location.href = CONSTANTS.LOGIN_URL;
      return Promise.reject(error);
    }

    if (
      error.response.status === 401 ||
      error.response.statusText.toString() === "Unauthorized"
    ) {
      auth.removeTokens();
      window.location.href = CONSTANTS.LOGIN_URL;
    }

    // specific error handling done elsewhere
    return Promise.reject(error);
  }
);

export class Api implements IApi {
  private readonly OK = 200;
  private readonly CREATED = 201;

  private readonly BADREQUEST = 400;
  private readonly UNAUTHORIZED = 401;
  private readonly FORBIDDEN = 403;
  private readonly NOTFOUND = 404;
  private readonly METHODNOTALLOWED = 405;
  private readonly NOTACCEPTABLE = 406;

  private readonly url: string;
  constructor(url: string) {
    this.url = url;
  }

  private prepareErrorResponse(result: any, message?: string): IApiResponse {
    if (result.response.status === this.UNAUTHORIZED) {
      const messages = this.prepareMessages(
        "You are not authorized to perform the action."
      );
      return new ApiResponse(messages);
    } else if (result.response.data === this.FORBIDDEN) {
      const messages = this.prepareMessages(
        "You are forbidden to perform the action."
      );
      return new ApiResponse(messages);
    } else if (result.response.status === this.NOTFOUND) {
      return this.prepareErrorData(result, message);
    } else if (result.response.status === this.BADREQUEST) {
      return this.prepareErrorData(result, message);
    }

    return new ApiResponse(["Uncatched exception: Internal server error!"]);
  }

  private prepareErrorData(result: any, message?: string): IApiResponse {
    if (
      result.response.data &&
      result.response.data.errors &&
      result.response.data.errors.Errors
    ) {
      const messages: string[] = [];
      const otherData: string[] = [];
      result.response.data.errors.Errors.forEach((element: string) => {
        const str = element.substring(1, element.length - 1);
        const elm = str.split(":");
        const eCode = APIErrorCode.ErrorCode.find(
          (x) => x["Message code "] === elm[0]
        );
        if (eCode !== undefined) {
          if (elm[1].length > 1) {
            const element = elm[1].substring(1, elm[1].length - 1);
            messages.push(eCode.Message);
            otherData.push(element);
          } else if (elm[1].length > 2) {
            const messageTwo = eCode.Message.format(elm[1][0], elm[1][1]);
            messages.push(messageTwo);
          } else {
            messages.push(eCode.Message);
          }
        } else if (eCode === undefined) {
          messages.push(element);
        } else {
          messages.push("Internal server error!");
        }
      });
      return new ApiResponse(messages, otherData);
    } else if (result.response.data && result.response.data.detail) {
      return new ApiResponse(this.prepareMessages(result.response.data.detail));
    } else {
      return new ApiResponse(this.prepareMessages(message));
    }
  }

  prepareMessages(message?: string): string[] {
    const messages: string[] = [];
    if (message) messages.push(message);
    return messages;
  }

  async licenseGet(request: IApiRequest): Promise<IApiResponse> {
    const config: AxiosRequestConfig = {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
    };

    let response: IApiResponse;
    let isError = false;
    const finalUrl = this.url + request.url;
    await axiosInstance
      .get(finalUrl, { ...config })
      .then((result) => {
        if (result.status === this.OK) {
          response = new ApiResponse([], result.data);
        }
      })
      .catch((result) => {
        isError = true;
        response = this.prepareErrorResponse(result, request.message);
      });

    return new Promise<IApiResponse>((resolve, reject) => {
      if (isError) reject(response);
      else resolve(response);
    });
  }

  async licensePost(request: IApiRequest): Promise<IApiResponse> {
    const config = {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
    };

    let response: IApiResponse;
    let isError = false;
    const finalUrl = this.url + request.url;
    await axiosInstance
      .post(finalUrl, request.data, { ...config })
      .then((result) => {
        if (result.status === this.CREATED || result.status === this.OK) {
          const messages = this.prepareMessages(request.message);
          response = new ApiResponse(messages, result.data, result.status);
        }
      })
      .catch((result) => {
        isError = true;
        response = this.prepareErrorResponse(result);
      });

    return new Promise<IApiResponse>((resolve, reject) => {
      if (isError) reject(response);
      else resolve(response);
    });
  }

  async login(request: IApiRequest): Promise<IApiResponse> {
    const config = {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
    };

    let response: IApiResponse;
    let isError = false;
    const finalUrl = this.url + request.url;
    await axios
      .post(finalUrl, request.data, config)
      .then((result) => {
        if (result.status === this.CREATED || result.status === this.OK) {
          const messages = this.prepareMessages(request.message);
          response = new ApiResponse(messages, result.data);
        }
      })
      .catch((result) => {
        isError = true;
        response = this.prepareErrorResponse(result);
      });

    return new Promise<IApiResponse>((resolve, reject) => {
      if (isError) reject(response);
      else resolve(response);
    });
  }

  async get(request: IApiRequest): Promise<IApiResponse> {
    const config: AxiosRequestConfig = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${auth.getAccessToken()}`,
      },
    };

    let response: IApiResponse;
    let isError = false;
    const finalUrl = this.url + request.url;
    await axiosInstance
      .get(finalUrl, { ...config })
      .then((result) => {
        if (result.status === this.OK) {
          response = new ApiResponse([], result.data);
        }
      })
      .catch((result) => {
        isError = true;
        response = this.prepareErrorResponse(result, request.message);
      });

    return new Promise<IApiResponse>((resolve, reject) => {
      if (isError) reject(response);
      else resolve(response);
    });
  }

  async post(request: IApiRequest): Promise<IApiResponse> {
    const config = {
      headers: {
        "Content-Type": "application/json",
        "x-requestid": request.requestId,
        Authorization: `Bearer ${auth.getAccessToken()}`,
      },
    };

    let response: IApiResponse;
    let isError = false;
    const finalUrl = this.url + request.url;
    await axiosInstance
      .post(finalUrl, request.data, { ...config })
      .then((result) => {
        if (result.status === this.CREATED || result.status === this.OK) {
          const messages = this.prepareMessages(request.message);
          response = new ApiResponse(messages, result.data, result.status);
        }
      })
      .catch((result) => {
        isError = true;
        response = this.prepareErrorResponse(result);
      });

    return new Promise<IApiResponse>((resolve, reject) => {
      if (isError) reject(response);
      else resolve(response);
    });
  }

  async put(request: IApiRequest): Promise<IApiResponse> {
    const config = {
      headers: {
        "Content-Type": "application/json",
        "x-requestid": request.requestId,
        Authorization: `Bearer ${auth.getAccessToken()}`,
      },
    };

    let response: IApiResponse;
    let isError = false;
    const finalUrl = this.url + request.url;

    await axiosInstance
      .put(finalUrl, request.data, { ...config })
      .then((result) => {
        if (result.status === this.OK) {
          const messages = this.prepareMessages(request.message);
          response = new ApiResponse(messages);
        }
      })
      .catch((result) => {
        isError = true;
        response = this.prepareErrorResponse(result);
      });

    return new Promise<IApiResponse>((resolve, reject) => {
      if (isError) reject(response);
      else resolve(response);
    });
  }

  async delete(request: IApiRequest): Promise<IApiResponse> {
    const config = {
      headers: {
        "Content-Type": "application/json",
        "x-requestid": request.requestId,
        Authorization: `Bearer ${auth.getAccessToken()}`,
      },
    };

    let response: IApiResponse;
    let isError = false;
    const finalUrl = this.url + request.url;

    await axiosInstance
      .delete(finalUrl, { ...config })
      .then((result) => {
        if (result.status === this.OK) {
          const messages = this.prepareMessages(request.message);
          response = new ApiResponse(messages);
        }
      })
      .catch((result) => {
        isError = true;
        response = this.prepareErrorResponse(result);
      });

    return new Promise<IApiResponse>((resolve, reject) => {
      if (isError) reject(response);
      else resolve(response);
    });
  }

  async deleteAll(request: IApiRequest): Promise<IApiResponse> {
    const config = {
      headers: {
        "Content-Type": "application/json",
        "x-requestid": request.requestId,
        Authorization: `Bearer ${auth.getAccessToken()}`,
      },
    };

    let response: IApiResponse;
    let isError = false;
    const finalUrl = this.url + request.url;
    await axiosInstance
      .delete(finalUrl, { data: request.data, ...config })
      .then((result) => {
        if (result.status === this.OK) {
          const messages = this.prepareMessages(request.message);
          response = new ApiResponse(messages, result.data);
        }
      })
      .catch((result) => {
        isError = true;
        response = this.prepareErrorResponse(result);
      });

    return new Promise<IApiResponse>((resolve, reject) => {
      if (isError) reject(response);
      else resolve(response);
    });
  }

  async patch(request: IApiRequest): Promise<IApiResponse> {
    const config = {
      headers: {
        "Content-Type": "application/json",
        "x-requestid": request.requestId,
        Authorization: `Bearer ${auth.getAccessToken()}`,
      },
    };

    let response: IApiResponse;
    let isError = false;
    const finalUrl = this.url + request.url;

    await axiosInstance
      .patch(finalUrl, request.data, { ...config })
      .then((result) => {
        if (result.status === this.OK) {
          const messages = this.prepareMessages(request.message);
          response = new ApiResponse(messages);
        }
      })
      .catch((result) => {
        isError = true;
        response = this.prepareErrorResponse(result);
      });

    return new Promise<IApiResponse>((resolve, reject) => {
      if (isError) reject(response);
      else resolve(response);
    });
  }

  async download(
    request: IApiRequest,
    id: string,
    contentType: string
  ): Promise<IApiResponse> {
    const config: AxiosRequestConfig = {
      headers: {
        "Content-Type": contentType,
        Authorization: `Bearer ${auth.getAccessToken()}`,
      },
      responseType: "blob",
    };

    let response: IApiResponse;
    let isError = false;
    const finalUrl = this.url + request.url;
    await axiosInstance
      .get(finalUrl, { ...config })
      .then((result) => {
        if (result.status === this.OK) {
          response = new ApiResponse([], result.data);
        }
      })
      .catch((result) => {
        isError = true;
        response = this.prepareErrorResponse(result, request.message);
      });

    return new Promise<IApiResponse>((resolve, reject) => {
      if (isError) reject(response);
      else resolve(response);
    });
  }

  

  async downloadPost(
    request: IApiRequest,
    id: string[],
    contentType: string
  ): Promise<IApiResponse> {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${auth.getAccessToken()}`,
      },
      responseType: "blob",
    };

    let response: IApiResponse;
    let isError = false;
    const finalUrl = this.url + request.url;
    await axiosInstance
      .post(finalUrl, request.data, config)
      .then((result) => {
        if (result.status === this.OK) {
          response = new ApiResponse([], result.data);
        }
      })
      .catch((result) => {
        isError = true;
        response = this.prepareErrorResponse(result, request.message);
      });

    return new Promise<IApiResponse>((resolve, reject) => {
      if (isError) reject(response);
      else resolve(response);
    });
  }

}
